import { fetchWithChatFeedback } from './chatFeedbackRequired';
/**
 * Business Impact API client helpers.
 *
 * Backed by three new endpoints (see spec §4 + implplan §4 / §7):
 *   - POST  /api/flows/{flowId}/business-impact/generate
 *   - PATCH /api/flows/{flowId}/business-impact
 *   - GET   /api/user/scoreboard-stats
 *
 * All calls attach the `X-User-Email` header sourced from
 * `localStorage.getItem('userEmail')` to mirror the auth pattern used by
 * other workflow API helpers in this folder (see `foprFunctions.ts`,
 * `stageFunctions.ts`).
 */

import { USER_FACING_ERROR_MESSAGES } from '../utils/userFacingError';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BusinessImpactGenerateResponse {
  summaryShort: string;
  summaryParagraph: string;
  generatedAt: string;
  sourceRevision?: string;
  status?: 'ready' | 'stale' | 'pending' | 'failed' | 'empty';
  stale?: boolean;
  reconciliationRequired?: boolean;
}

export interface BusinessImpactPatchPayload {
  summaryShort?: string;
  summaryParagraph?: string;
}

export interface BusinessImpactPatchResponse {
  summaryShort?: string;
  summaryParagraph?: string;
  editedAt: string;
}

export interface ScoreboardProject {
  id: string;
  name: string;
  workflowType: string;
  impactShort: string | null;
  completionSeconds: number;
}

export interface ScoreboardStats {
  previousLogin: string | null;
  completedCount: number;
  /** Total number of OWNED flows for the user, any status. */
  totalCount: number;
  medianCompletionSeconds: number | null;
  projects: ScoreboardProject[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getUserEmail(): string {
  return localStorage.getItem('userEmail') || (window as any).userEmail || '';
}

function buildHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const userEmail = getUserEmail();
  if (userEmail) {
    headers['X-User-Email'] = userEmail;
  }
  return headers;
}

async function parseError(response: Response): Promise<string> {
  try {
    const data = await response.json();
    if (data?.detail && typeof data.detail === 'object' && 'error' in data.detail) {
      if (data.detail.error) return data.detail.error;
    }
    if (typeof data?.detail === 'string') return data.detail;
    if (typeof data?.error === 'string') return data.error;
    if (typeof data?.message === 'string') return data.message;
  } catch {
    /* fall through */
  }
  // Never surface raw HTTP status codes or other technical detail to the user.
  // Use the canonical maintainers_alerted message so this error is already
  // on the allowlist by the time it reaches toUserFacingError() at the JSX
  // render boundary.
  return USER_FACING_ERROR_MESSAGES.maintainers_alerted;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * Trigger LLM generation of the business-impact summary + paragraph for a
 * flow. Backend reads BI answers server-side from the existing per-stage
 * answer store; no payload is required from the client.
 */
export async function generateBusinessImpact(
  flowId: string
): Promise<BusinessImpactGenerateResponse> {
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/flows/${flowId}/business-impact/generate`,
    {
      method: 'POST',
      headers: buildHeaders(),
      credentials: 'include',
    }
  );
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
  return response.json();
}

/**
 * Persist a user edit to the short summary and/or long paragraph. Backend
 * sets `editedAt` to "now"; `generatedAt` is left untouched.
 */
export async function patchBusinessImpact(
  flowId: string,
  payload: BusinessImpactPatchPayload
): Promise<BusinessImpactPatchResponse> {
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/flows/${flowId}/business-impact`,
    {
      method: 'PATCH',
      headers: buildHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload),
    }
  );
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
  return response.json();
}

/**
 * Fetch the current user's scoreboard stats (Workflow Selector page footer).
 * Owner-only — flows shared with the user do NOT count.
 */
export async function getScoreboardStats(): Promise<ScoreboardStats> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/user/scoreboard-stats`, {
    method: 'GET',
    headers: buildHeaders(),
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
  return response.json();
}
